import { useCallback, useEffect, useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

import { notification } from '@webclient/components/notification';
import PLANS from '@webclient/components/plans/config';
import RHFInputField from '@webclient/components/react-hook-form/RHFInputField';
import useGetSubscriptionLazily from '@webclient/components/user-authentication/hooks/useGetSubscription';
import useLookupUserEmail from '@webclient/components/user-authentication/hooks/useLookupEmail';
import useUserAuth from '@webclient/components/user-authentication/hooks/useUserAuth';
import { enterEmailSchema } from '@webclient/components/user-authentication/schemas';
import BaseStep from '@webclient/components/user-authentication/sections/steps/components/base-step';
import SsoLogin from '@webclient/components/user-authentication/sections/steps/components/sso-login';
import WordDivider from '@webclient/components/user-authentication/sections/steps/components/word-divider';

import { getDecodedToken } from '@webclient/lib/jwt';

import { useEnabledAccessConfigurations } from '@webclient/hooks/useAccessConfigurations';
import useAppDispatch from '@webclient/hooks/useAppDispatch';
import useAppSelector from '@webclient/hooks/useAppSelector';
import useQueryParams from '@webclient/hooks/useQueryParams';
import useResponsive from '@webclient/hooks/useResponsive';
import useWorkspace from '@webclient/hooks/useWorkspace';

import { setAttemptedToLogin } from '@webclient/state/slices/auth';

import { StripePaymentStatusType } from '@webclient/shared-types/payment';

import UserLoginIcon from '~icons/knowz-iconify/user-login';

import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslate } from '@tolgee/react';
import sendCreditsPurchasedToAnalytics from '@webclient/analytics/sendCreditsPurchasedToAnalytics';
import { isAxiosError } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useForm } from 'react-hook-form';

type EnterEmailFormType = {
  email: string;
};

const CODE_VERIFICATION_STEP = 1;

export default function EnterEmail() {
  const { t } = useTranslate();

  const isItMobileOrTablet = useResponsive('down', 'md');

  const {
    handleIsUserAlreadyRegistered,
    handleSetEmail,
    handleNextStep,
    handleCustomStep,
    handleAuthenticationJourney,
    resetForm,
    shouldBackToEnterEmail,
  } = useUserAuth();

  const { accessConfigurationsData, isLoadingAccessConfigurations } =
    useEnabledAccessConfigurations();
  const dispatch = useAppDispatch();
  const attemptedToLogin = useAppSelector(
    (state) => state.auth.attemptedToLogin,
  );
  const token = window.localStorage.getItem('token');

  const decodedToken = getDecodedToken(token);

  const { resetWorkspaceToIndividualRole } = useWorkspace();

  const { getQueryParamByKey, removeAllQueryParamsNoMatterWhat } =
    useQueryParams();

  const subscriptionQueryParam =
    getQueryParamByKey<StripePaymentStatusType>('subscription');

  const paymentQueryParam =
    getQueryParamByKey<StripePaymentStatusType>('payment');
  const priceIdQueryParam = getQueryParamByKey<string | null>('price_id');

  const { data: subscriptionData } =
    useGetSubscriptionLazily(priceIdQueryParam);

  const invitationToken = getQueryParamByKey<string | null>('token');

  const decodedInvitationToken = invitationToken
    ? jwtDecode<{
        membership_id: string;
        email_invite: string;
      }>(invitationToken)
    : null;

  const shouldPreFillEmailForPaidUsers =
    subscriptionQueryParam === 'success' && decodedToken?.paid;

  const shouldPreFillEmailForExpiredToken =
    decodedToken?.auth_type === 'email' && !attemptedToLogin;

  const shouldPreFillEmailForWorkspaceInvitation = !!decodedInvitationToken;

  const defaultValues = {
    email:
      !shouldPreFillEmailForWorkspaceInvitation &&
      (shouldPreFillEmailForExpiredToken || shouldPreFillEmailForPaidUsers)
        ? decodedToken?.email
        : shouldPreFillEmailForWorkspaceInvitation
          ? decodedInvitationToken?.email_invite
          : '',
  };

  const [displayAllSso, setDisplayAllSso] = useState(false);

  const methods = useForm<EnterEmailFormType>({
    resolver: yupResolver(enterEmailSchema(t)),
    defaultValues,
  });

  const closeModal = useCallback(() => {
    removeAllQueryParamsNoMatterWhat();
    resetForm();
    dispatch(setAttemptedToLogin(true));
    resetWorkspaceToIndividualRole();
  }, [
    dispatch,
    removeAllQueryParamsNoMatterWhat,
    resetForm,
    resetWorkspaceToIndividualRole,
  ]);

  useEffect(() => {
    //? check to see if user coming from a payment page
    if (priceIdQueryParam == null) return;

    const wasItSuccessful =
      subscriptionQueryParam === 'success' || paymentQueryParam === 'success';
    const associatedPlan = PLANS[priceIdQueryParam];

    if (decodedToken == null) return;

    if (associatedPlan && subscriptionData) {
      sendCreditsPurchasedToAnalytics({
        userUUID: decodedToken.uuid,
        transactionId: subscriptionData.externalId,
        currency: subscriptionData.currency,
        status: wasItSuccessful ? 'success' : 'abort',
        item: {
          price: associatedPlan.price,
          plan: associatedPlan.title,
          id: subscriptionData.id,
        },
      });
    }

    if (decodedToken.auth_type !== 'web_fingerprint' || shouldBackToEnterEmail)
      return;
    handleAuthenticationJourney('register');
    handleCustomStep(CODE_VERIFICATION_STEP);
    handleSetEmail(decodedToken.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionData]);

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
  } = methods;

  const {
    mutateAsync: lookupUserEmailAsync,
    isPending: isLookupUserEmailPending,
  } = useLookupUserEmail();

  const isBusy = isSubmitting || isLookupUserEmailPending;

  const isThereAnySSOConfig =
    !isLoadingAccessConfigurations &&
    accessConfigurationsData &&
    accessConfigurationsData.data.length > 0;

  async function onSubmit(data: EnterEmailFormType) {
    try {
      const lookupEmailResponse = await lookupUserEmailAsync(data);
      if (
        lookupEmailResponse.status === 226 &&
        lookupEmailResponse.data.status === 'Im Used'
      ) {
        handleSetEmail(data.email);
        handleIsUserAlreadyRegistered(true);
        handleNextStep();
        handleAuthenticationJourney('login');
        return;
      }
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        handleSetEmail(data.email);
        handleIsUserAlreadyRegistered(false);
        handleNextStep();
        handleAuthenticationJourney('register');
        return;
      }
      notification.error({
        message: t(
          'response.errors.code.520',
          'Something went wrong. Please try again later.',
        ),
      });
    }
  }

  return (
    <BaseStep
      slots={{
        header: displayAllSso
          ? undefined
          : {
              onClick: isItMobileOrTablet ? closeModal : undefined,
              heading: t('layout.auth.steps.enterEmail.label', 'Get started'),
              description: t(
                'layout.auth.steps.enterEmail.description',
                'We’ll check if you have an account, and help create one if you don’t.',
              ),
            },

        form: {
          methods: methods,
          onSubmit: handleSubmit(onSubmit),
        },
      }}
    >
      {!displayAllSso && (
        <>
          <RHFInputField
            type="email"
            disabled={isBusy}
            placeholder={t(
              'layout.auth.steps.enterEmail.form.email.placeholder',
              'Enter your email',
            )}
            fullWidth
            name="email"
            autoComplete="username" //? https://www.chromium.org/developers/design-documents/form-styles-that-chromium-understands/
            error={!!errors.email?.message}
            helperText={errors.email?.message}
          />

          <LoadingButton
            disabled={isBusy}
            loading={isBusy}
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            startIcon={<UserLoginIcon />}
          >
            {t('layout.auth.buttons.continue', 'Continue')}
          </LoadingButton>

          {isThereAnySSOConfig && (
            <Stack
              gap={4}
              sx={{ marginBlockStart: 1 }}
            >
              <WordDivider
                text={t('layout.auth.steps.enterEmail.form.or', 'or')}
              />
              <Stack gap={3}>
                <SsoLogin
                  onLogin={() => closeModal()}
                  displayAll={displayAllSso}
                />

                <Button
                  onClick={() => setDisplayAllSso(true)}
                  variant="outlined"
                  color="secondary"
                  startIcon={<KeyboardArrowDownIcon />}
                >
                  {t(
                    'layout.auth.steps.enterEmail.showMore',
                    'Show more options',
                  )}
                </Button>
              </Stack>
            </Stack>
          )}
        </>
      )}

      {displayAllSso && (
        <Stack
          gap={5}
          sx={{ marginBlockStart: 5 }}
        >
          <Button
            onClick={() => setDisplayAllSso(false)}
            variant="outlined"
            color="secondary"
            startIcon={<KeyboardArrowUpIcon />}
          >
            {t('layout.auth.steps.enterEmail.showLess', 'Show less options')}
          </Button>

          <SsoLogin
            onLogin={() => closeModal()}
            displayAll={displayAllSso}
          />
        </Stack>
      )}
    </BaseStep>
  );
}
